import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { IcoChevronRight16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { LinkButton, Tabs } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, Fragment, type ReactNode, type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUSINESS_INSIGHT_SUBSECTIONS, CardError, CardSkeleton, EncryptedBusinessInsights, getOnboardingQueries, getRelevantDis, groupRiskSignals, isAwaitingResults, ObeRules, OnboardingDocuments, OnboardingFieldValidations, OnboardingRiskSignals, OnboardingRules, OnboardingVaultData, Subsection as SubsectionBody, type SubsectionType } from '@/components/entity-onboarding-details';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import type { WithEntityProps } from '@/entity/components/with-entity';
import DeviceInsights from '../../../device-insights/device-insights';
import DuplicateData from '../../../duplicate-data';
import useShowOnboarding from '../../../onboardings-shared/hooks/use-show-onboarding';
import { pinScrollPosition } from '../../../onboardings-shared/utils';
import OtherInsights from '../../../other-insights';
import type { SectionKey } from '../../onboardings.types';
import OnboardingSection, { SECTION_ICONS } from '../onboarding-section';
import BusinessInsights from './components/business-insights';
import OnboardingDataHeader from './components/onboarding-data-header';
import useSectionCounts from './hooks/use-section-counts';
import useSections from './hooks/use-sections';
type LiveSectionKey = Extract<SectionKey, 'duplicate-data' | 'device-insights' | 'other-insights'>;
type TabKey = 'data' | 'rules' | 'business' | 'devices';

// Links name sections as the classic layout does, which splits business insights into its parts.
const toLinkedSection = (key: SectionKey): SubsectionType => key === 'business-insights' ? 'business-details' : key;
const isLinkedSection = (key: SectionKey, linked: SubsectionType | null) => key === 'business-insights' ? BUSINESS_INSIGHT_SUBSECTIONS.some(section => section === linked) : key === linked;
const TABS: {
  key: TabKey;
  sections: SectionKey[];
}[] = [{
  key: 'rules',
  sections: ['rules', 'risk-signals', 'field-validations']
}, {
  key: 'business',
  sections: ['business-insights']
}, {
  key: 'devices',
  sections: ['device-insights', 'other-insights']
},
// Documents are a card in the data section, but links still name them and must open this tab.
{
  key: 'data',
  sections: ['user-data', 'business-data', 'documents', 'duplicate-data']
}];
type OnboardingDataProps = WithEntityProps & {
  onboarding: EntityOnboarding;
  onViewCurrent?: (key: LiveSectionKey) => void;
  sectionRef?: (key: SectionKey) => Ref<HTMLElement>;
};
const OnboardingData = t0 => {
  const $ = _c(54);
  const {
    entity,
    onboarding,
    onViewCurrent,
    sectionRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  let t2;
  if ($[1] !== entity || $[2] !== onboarding) {
    t2 = getOnboardingQueries(entity, onboarding);
    $[1] = entity;
    $[2] = onboarding;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queries = t2;
  const {
    data: riskSignals,
    error: riskSignalsError
  } = useQuery(queries.riskSignals);
  const {
    data: entityAttributes,
    isPending: isAttributesPending,
    error: attributesError
  } = useQuery(queries.attributes);
  const {
    data: onboardingTrace,
    error: traceError
  } = useQuery(queries.trace);
  let t3;
  if ($[4] !== entity.id || $[5] !== entityAttributes || $[6] !== onboarding.seqno) {
    t3 = {
      entityAttributes,
      entityId: entity.id,
      seqno: onboarding.seqno
    };
    $[4] = entity.id;
    $[5] = entityAttributes;
    $[6] = onboarding.seqno;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const entityVault = useEntityVault(t3);
  const sections = useSections(onboarding);
  const counts = useSectionCounts(entity, onboarding);
  const {
    onboardingSection: requestedSection,
    setOnboardingSection
  } = useShowOnboarding();
  const tabTitles = {
    data: t(_temp),
    rules: t(_temp2),
    business: t(_temp3),
    devices: t(_temp4)
  };
  const tabs = TABS.map(t4 => {
    const {
      key,
      sections: keys
    } = t4;
    return {
      key,
      title: tabTitles[key],
      sections: sections.filter(section => keys.includes(section.key))
    };
  }).filter(_temp5);
  let t5;
  if ($[8] !== requestedSection) {
    t5 = TABS.find(t6 => {
      const {
        sections: keys_0
      } = t6;
      return keys_0.some(key_0 => isLinkedSection(key_0, requestedSection));
    });
    $[8] = requestedSection;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const requestedTab = t5;
  const tab_0 = tabs.find(candidate => candidate.key === requestedTab?.key) ?? tabs[0];
  const tabBarRef = useRef(null);
  const tabPanelRef = useRef(null);
  const isResettingScrollRef = useRef(false);
  const [tabBarHeight, setTabBarHeight] = useState();
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const tabBar = tabBarRef.current;
      if (!tabBar) {
        return;
      }
      const observer = new ResizeObserver(t8 => {
        const [entry] = t8;
        return setTabBarHeight(entry.borderBoxSize[0]?.blockSize);
      });
      observer.observe(tabBar);
      return () => observer.disconnect();
    };
    t7 = [];
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  const selectTab = key_1 => {
    const next = tabs.find(candidate_0 => candidate_0.key === key_1);
    if (!next) {
      return;
    }
    const tabBarBottom = tabBarRef.current?.getBoundingClientRect().bottom ?? 0;
    isResettingScrollRef.current = (tabPanelRef.current?.getBoundingClientRect().top ?? 0) < tabBarBottom;
    setOnboardingSection(toLinkedSection(next.sections[0].key));
  };
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const panel = tabPanelRef.current;
      const tabBar_0 = tabBarRef.current;
      if (!isResettingScrollRef.current || !panel || !tabBar_0) {
        return;
      }
      isResettingScrollRef.current = false;
      return pinScrollPosition(panel, tabBar_0.getBoundingClientRect().bottom);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useLayoutEffect(t8, [tab_0.key]);
  let t9;
  if ($[13] !== entityAttributes) {
    t9 = entityAttributes?.some(_temp6);
    $[13] = entityAttributes;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const hasDecryptableDIs = Boolean(t9);
  let t10;
  if ($[15] !== entityAttributes || $[16] !== entityVault || $[17] !== hasDecryptableDIs) {
    t10 = hasDecryptableDIs && getRelevantDis((entityAttributes ?? []).filter(_temp7).map(_temp8)).every(di => entityVault.isDecrypted(di));
    $[15] = entityAttributes;
    $[16] = entityVault;
    $[17] = hasDecryptableDIs;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const isVaultDecrypted = t10;
  const showVaultDecrypt = hasDecryptableDIs ? !isVaultDecrypted : Boolean(entityAttributes?.length);
  const canDecryptVault = showVaultDecrypt && hasDecryptableDIs;
  const asOfNote = onboarding.completedAt ? t(_temp9, {
    date: formatDateWithTime(new Date(onboarding.completedAt), {
      month: "short",
      year: "numeric"
    })
  }) : t(_temp10);
  let t11;
  if ($[19] !== onViewCurrent || $[20] !== t) {
    t11 = (note, liveKey) => <div className="flex flex-wrap items-center gap-x-3 gap-y-1"><p className="text-body-3 text-tertiary">{note}</p>{liveKey && onViewCurrent && <LinkButton variant="label-3" iconComponent={IcoChevronRight16} onClick={() => onViewCurrent(liveKey)}>{t(_temp11)}</LinkButton>}</div>;
    $[19] = onViewCurrent;
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const renderMoment = t11;
  const getAction = key_2 => {
    switch (key_2) {
      case "duplicate-data":
      case "device-insights":
        {
          return renderMoment(asOfNote, onboarding.completedAt ? key_2 : undefined);
        }
      case "other-insights":
        {
          return renderMoment(t(_temp12), key_2);
        }
      default:
        {
          return null;
        }
    }
  };
  let t12;
  if ($[22] !== attributesError || $[23] !== entity || $[24] !== entityVault || $[25] !== hasDecryptableDIs || $[26] !== isAttributesPending || $[27] !== isVaultDecrypted || $[28] !== onboarding || $[29] !== onboardingTrace || $[30] !== riskSignals || $[31] !== riskSignalsError || $[32] !== sectionRef || $[33] !== traceError) {
    t12 = key_3 => {
      switch (key_3) {
        case "user-data":
        case "business-data":
          {
            return <div className="flex flex-col gap-3 p-5"><OnboardingVaultData entity={entity} vault={entityVault} isLoading={isAttributesPending} error={attributesError} /><OnboardingDocuments ref={sectionRef?.("documents")} entity={entity} onboarding={onboarding} /></div>;
          }
        case "rules":
          {
            if (!onboarding.hasGraphTrace) {
              return <OnboardingRules entity={entity} onboarding={onboarding} />;
            }
            if (onboardingTrace) {
              return <ObeRules trace={onboardingTrace} isAwaitingRules={isAwaitingResults(onboarding)} />;
            }
            if (traceError) {
              return <SubsectionBody><CardError error={traceError} /></SubsectionBody>;
            }
            return <SubsectionBody><CardSkeleton rows={1} /></SubsectionBody>;
          }
        case "risk-signals":
          {
            return <OnboardingRiskSignals riskSignals={riskSignals && groupRiskSignals(riskSignals)} error={riskSignalsError} />;
          }
        case "field-validations":
          {
            return <SubsectionBody><OnboardingFieldValidations entity={entity} onboarding={onboarding} /></SubsectionBody>;
          }
        case "duplicate-data":
          {
            return <SubsectionBody><DuplicateData timestamp={onboarding.completedAt} /></SubsectionBody>;
          }
        case "business-insights":
          {
            return isVaultDecrypted ? <BusinessInsights onboarding={onboarding} /> : <EncryptedBusinessInsights canDecrypt={hasDecryptableDIs} vaultData={entityVault.data} error={attributesError} />;
          }
        case "device-insights":
          {
            return <div className="p-5"><DeviceInsights entity={entity} asOf={onboarding.completedAt} /></div>;
          }
        case "other-insights":
          {
            return <SubsectionBody><OtherInsights onboardingId={onboarding.id} /></SubsectionBody>;
          }
      }
    };
    $[22] = attributesError;
    $[23] = entity;
    $[24] = entityVault;
    $[25] = hasDecryptableDIs;
    $[26] = isAttributesPending;
    $[27] = isVaultDecrypted;
    $[28] = onboarding;
    $[29] = onboardingTrace;
    $[30] = riskSignals;
    $[31] = riskSignalsError;
    $[32] = sectionRef;
    $[33] = traceError;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const renderSection = t12;
  let t13;
  if ($[35] !== canDecryptVault || $[36] !== isAttributesPending || $[37] !== showVaultDecrypt) {
    t13 = isAttributesPending ? undefined : {
      isNeeded: showVaultDecrypt,
      canDecrypt: canDecryptVault
    };
    $[35] = canDecryptVault;
    $[36] = isAttributesPending;
    $[37] = showVaultDecrypt;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== entity || $[40] !== entityVault || $[41] !== onboarding || $[42] !== t13) {
    t14 = <OnboardingDataHeader entity={entity} onboarding={onboarding} vault={entityVault} vaultDecrypt={t13} />;
    $[39] = entity;
    $[40] = entityVault;
    $[41] = onboarding;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const t15 = tab_0.key;
  const t16 = "tabpanel";
  const t17 = tab_0.title;
  const t18 = "flex flex-col gap-8 px-5 pt-5 pb-6 transition-opacity duration-150 ease-out starting:opacity-0 motion-reduce:transition-none";
  let t19;
  if ($[44] !== tabBarHeight) {
    t19 = tabBarHeight ? {
      "--onboardings-tabs-height": `${tabBarHeight}px`
    } as CSSProperties : undefined;
    $[44] = tabBarHeight;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const T0 = Fragment;
  const t20 = onboarding.id;
  const t22 = tab_0.sections.map(t21 => {
    const {
      key: key_5,
      title: title_0
    } = t21;
    return <OnboardingSection key={key_5} ref={sectionRef?.(key_5)} title={title_0} icon={SECTION_ICONS[key_5]} count={counts[key_5]} action={getAction(key_5)}>{renderSection(key_5)}</OnboardingSection>;
  });
  let t23;
  if ($[46] !== T0 || $[47] !== onboarding.id || $[48] !== t22) {
    t23 = <T0 key={t20}>{t22}</T0>;
    $[46] = T0;
    $[47] = onboarding.id;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t19 || $[51] !== t23 || $[52] !== tab_0) {
    t24 = <div key={t15} ref={tabPanelRef} role={t16} aria-label={t17} className={t18} style={t19}>{t23}</div>;
    $[50] = t19;
    $[51] = t23;
    $[52] = tab_0;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  return <>{t14}<div ref={tabBarRef} className="z-10 -mt-1 bg-primary px-5 pt-1 md:sticky md:top-[calc(var(--onboardings-strip-height,4rem)-1.75rem)]"><Tabs value={tab_0.key} onChange={selectTab} options={tabs.map(_temp13)} /></div>{t24}</>;
};
export default OnboardingData;
function _temp($) {
  return $.tabs.data;
}
function _temp2($_0) {
  return $_0.tabs.rules;
}
function _temp3($_1) {
  return $_1.tabs.business;
}
function _temp4($_2) {
  return $_2.tabs.deviceInsights;
}
function _temp5(tab) {
  return tab.sections.length > 0;
}
function _temp6(attr) {
  return attr.isDecryptable;
}
function _temp7(attr_0) {
  return attr_0.isDecryptable;
}
function _temp8(attr_1) {
  return attr_1.identifier;
}
function _temp9($_3) {
  return $_3.snapshot.asOf;
}
function _temp10($_4) {
  return $_4.snapshot.unfinished;
}
function _temp11($_5) {
  return $_5.snapshot.viewCurrent;
}
function _temp12($_6) {
  return $_6.snapshot.fromOnboarding;
}
function _temp13(t0) {
  const {
    key: key_4,
    title
  } = t0;
  return {
    label: title,
    value: key_4
  };
}